import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map } from 'rxjs';

import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { TranslateModule } from '@ngx-translate/core';

import { ERadioGroupType } from '@ui/sp-radio-group';
import { CSS_RESTRICTIONS } from '@common/constants';
import { EHorizontalPosition, type ESize, Widgets } from '@common/enums';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';

import type { NormalizedSpecificBlockEntity, WebLegacyWidgetEntity, WebWidgetModel } from '@web/types';
import type { ButtonEntity, Side, SocialEntity, TextEntity } from '@sites/entity-core';
import { atDevice } from '@sites/entity-core';
import type { BlockSpecificForm } from '@sites/entity-signal-form';
import type { NavigationWidgetData } from '@web/widgets/navigation/type';
import type { ChatBotWidgetData } from '@web/widgets/chat-bot/type';
import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type';

import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { SpSwitchComponent } from '@libs/ui/sp-switch/src/sp-switch.component';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpRadioGroupComponent } from '@libs/ui/sp-radio-group/src/sp-radio-group.component';
import { SpInputNumComponent } from '@libs/components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpControlHandleComponent } from '@libs/ui/sp-control-handle/src/sp-control-handle.component';
import { widgetNameOf } from '@common/helpers/widget-envelope';

/** One row of the "what shows in the collapsed menu" list. */
export type HeaderWidgetToggle = {
    id: string;
    name: string;
    label: string;
    subtext: string;
    isVisible: boolean;
};

enum Tab {
    View = 'view',
    Content = 'content',
}

/**
 * The header's mobile screen. It edits the same block signal form the parent
 * panel does — the form is passed down rather than re-resolved, so both screens
 * write into one entry of the history batch.
 */
@Component({
    selector: 'web-specific-block-mobile-settings',
    templateUrl: './mobile-settings.component.html',
    styleUrls: ['./mobile-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpSwitchComponent,
        WebColorPickerComponent,
        SpRadioGroupComponent,
        SpInputNumComponent,
        SpControlHandleComponent,
        CdkDragHandle,
        TranslateModule,
    ],
})
export class SpecificBlockMobileSettingsComponent {
    public readonly entity = input.required<NormalizedSpecificBlockEntity>();
    public readonly form = input.required<BlockSpecificForm>();
    public readonly cancelEvent = output<void>();

    private readonly webWidgetsService = inject(WebWidgetsService);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);

    public readonly tabEnum = Tab;
    public readonly currentTab = signal<Tab>(Tab.View);

    public readonly EHorizontalPosition = EHorizontalPosition;
    public readonly radioGroupType = ERadioGroupType;

    public readonly PADDING_MIN = CSS_RESTRICTIONS.BLOCK_PADDING_MIN;
    public readonly PADDING_MAX = CSS_RESTRICTIONS.BLOCK_PADDING_MAX;

    public readonly iconPositionType = toSignal(this.shopCartIconFacade.iconPositionType$, { initialValue: 'site' });
    public readonly isCartEnabled = toSignal(this.shopCartIconFacade.isCartEnabled$, { initialValue: false });

    private readonly entities = toSignal(
        combineLatest([
            this.sectionsFacade.sectionsEntities$,
            this.columnsFacade.columnsEntities$,
            this.widgetsFacade.widgetsEntities$,
        ]).pipe(map(([sections, columns, widgets]) => ({ sections, columns, widgets }))),
        { initialValue: { sections: {}, columns: {}, widgets: {} } as never }
    );

    // --- Reads ----------------------------------------------------------------

    public readonly hideContentOnMobile = computed<boolean>(() => Boolean(this.form().hideContentOnMobile().value()));

    public readonly backgroundColor = computed<string>(() => {
        const color = atDevice(this.form().background.color().value(), 'mobile');

        return color ?? '';
    });

    public readonly textColor = computed<string>(() => String(this.form().textColor().value() ?? ''));
    public readonly iconColor = computed<string>(() => String(this.form().icon().value()?.color ?? ''));
    public readonly iconSize = computed<string>(() => String(this.form().icon().value()?.size ?? ''));
    public readonly iconHorizontalPosition = computed<string>(() =>
        String(this.form().icon().value()?.horizontalPosition ?? '')
    );
    public readonly horizontalPosition = computed<string>(() => String(this.form().horizontalPosition().value() ?? ''));

    public readonly padding = computed(() => atDevice(this.form().padding().value(), 'mobile')?.value ?? null);

    /**
     * The header's own widgets, each with the label and the summary the list
     * shows. The cart icon is excluded: it has a panel of its own.
     */
    public readonly widgetToggles = computed<HeaderWidgetToggle[]>(() => {
        const { sections, columns, widgets } = this.entities() as {
            sections: Record<string, { children: string[] }>;
            columns: Record<string, { children: { id: string }[] }>;
            widgets: Record<string, WebLegacyWidgetEntity>;
        };
        const hidden = (this.form().hideWidgetsOnMobile().value() ?? {}) as Record<string, boolean>;

        return (this.entity()?.children ?? [])
            .map((sectionId: string) => sections[sectionId])
            .filter(Boolean)
            .flatMap((section) => section.children ?? [])
            .map((columnId: string) => columns[columnId])
            .filter(Boolean)
            .flatMap((column) => column.children ?? [])
            .map(({ id }) => widgets[id])
            .filter(
                (widget): widget is WebLegacyWidgetEntity =>
                    Boolean(widget) && widgetNameOf(widget) !== Widgets.SHOP_CART_ICON
            )
            .map((widget) => ({
                id: widget.id,
                name: widgetNameOf(widget) as string,
                label: this.webWidgetsService.getWidgetModel(widgetNameOf(widget) as Widgets)?.label ?? '',
                subtext: this.getWidgetSubtext(widget as never),
                isVisible: !hidden[widget.id],
            }));
    });

    // TODO: replace this method with `renderProp`
    private getWidgetSubtext(widget: WebWidgetModel): string {
        if (widget.name === Widgets.BUTTON) {
            return (widget.data as ButtonEntity['data']).buttons.map((button) => button.label).join(', ');
        }

        if (widget.name === Widgets.NAVIGATION) {
            return (widget.data as NavigationWidgetData).menuList.map((item) => item.name).join(', ');
        }

        if (widget.name === Widgets.CHAT_BOTS) {
            return (widget.data as ChatBotWidgetData).chatBots.map((item) => item.name).join(', ');
        }

        if (widget.name === Widgets.SOCIAL_NETWORKS) {
            return (widget.data as SocialEntity['data']).socials.map((item) => item.name).join(', ');
        }

        if (widget.name === Widgets.PAYMENT_FORM) {
            return (widget.data as PaymentFormWidgetData).formButtonSettings.label;
        }

        if (widget.name === Widgets.TEXT) {
            return (widget.data as TextEntity['data']).content.replace(/<[^>]*>/g, '');
        }

        return '';
    }

    // --- Writes ---------------------------------------------------------------

    public setHideContentOnMobile(value: boolean): void {
        this.form().setHideContentOnMobile(value);

        // With the burger off there is nothing to list on the content tab.
        if (!value) {
            this.currentTab.set(Tab.View);
        }
    }

    public setCurrentTab(tab: Tab): void {
        this.currentTab.set(tab);
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form().setBackgroundColor(presetName ?? value ?? null, 'mobile');
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form().setTextColor(presetName ?? value);
    }

    public setIconColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form().setIcon({ color: presetName ?? value });
    }

    public setIconSize(size: ESize): void {
        this.form().setIcon({ size });
    }

    public setIconHorizontalPosition(position: EHorizontalPosition): void {
        this.form().setIcon({ horizontalPosition: position });
    }

    public setHorizontalPosition(position: EHorizontalPosition): void {
        this.form().setHorizontalPosition(position);
    }

    public setPadding(side: Side, value: number): void {
        this.form().setPadding({ [side]: value }, 'mobile');
    }

    /** The list checkbox reads "shown", the stored flag reads "hidden". */
    public toggleWidget(widgetId: string, isVisible: boolean): void {
        this.form().setWidgetHiddenOnMobile(widgetId, !isVisible);
    }

    public onCancel(): void {
        this.cancelEvent.emit();
    }
}
